import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, ClockIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { TYPE_LABELS, PayloadDetail, payloadSummary, fmtDateDMY } from '../requestDisplay';

const fmtDate = fmtDateDMY;

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .cra * { box-sizing:border-box; }
  .cra { font-family:'Sora',sans-serif; color:#1a1d2e; animation:craIn .35s ease both; }
  @keyframes craIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
  .cra-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .cra-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .cra-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-green { background:#10c98e; color:#fff; } .btn-green:hover { background:#0db87e; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .stat-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-value { font-size:1.4rem; font-weight:700; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .tabs { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.2rem; width:fit-content; overflow-x:auto; scrollbar-width:none; }
  .tabs::-webkit-scrollbar { display:none; }
  .tab { padding:6px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; background:transparent; color:#9094a8; white-space:nowrap; }
  .tab.on { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .tbl tbody tr:last-child { border-bottom:none; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .pill { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .pill svg { width:11px; height:11px; }
  .pill-pending  { background:#fffbeb; color:#f59e0b; }
  .pill-approved { background:#ecfdf5; color:#10c98e; }
  .pill-rejected { background:#fef2f2; color:#ef4444; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#6b7194; }
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:520px; max-height:90vh; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; flex-shrink:0; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; flex:1; overflow-y:auto; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }
  .payload-box { background:#f8f9fc; border:1px solid #e8ebf4; border-radius:10px; padding:12px 14px; font-size:.76rem; color:#4a5075; line-height:1.7; margin-bottom:14px; }
  .payload-box b { color:#0f1123; }
  .payload-row { padding:2px 0; }
  .notice-box { border-radius:10px; padding:10px 14px; font-size:.78rem; margin-bottom:14px; }
  .notice-approve { background:#ecfdf5; border:1px solid #a7f3d0; color:#065f46; }
  .notice-reject  { background:#fef2f2; border:1px solid #fecaca; color:#991b1b; }
  .ff { display:flex; flex-direction:column; gap:5px; }
  .fl { font-size:.7rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.06em; }
  .ft { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; resize:vertical; min-height:80px; }
  .ft:focus { border-color:#3b6ef8; background:white; }
  .ac-enable  { background:#ecfdf5; color:#065f46; padding:2px 8px; border-radius:99px; font-size:.7rem; font-weight:600; }
  .ac-disable { background:#fef2f2; color:#991b1b; padding:2px 8px; border-radius:99px; font-size:.7rem; font-weight:600; }
  .exit-resigned   { background:#fffbeb; color:#d97706; padding:2px 8px; border-radius:99px; font-size:.7rem; font-weight:600; }
  .exit-terminated { background:#fef2f2; color:#ef4444; padding:2px 8px; border-radius:99px; font-size:.7rem; font-weight:600; }

  .cra-cards { display:none; padding:10px; }
  .cra-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .cra-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .cra-card-type { font-size:.84rem; font-weight:700; color:#0f1123; }
  .cra-card-by { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .cra-card-row { display:flex; justify-content:space-between; align-items:flex-start; font-size:.78rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .cra-card-row:last-of-type { border-bottom:none; }
  .cra-card-label { color:#9094a8; font-weight:600; flex-shrink:0; }
  .cra-card-summary { color:#4a5075; text-align:right; }
  .cra-card-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; }

  @media (max-width:768px) {
    .stat-grid { grid-template-columns:1fr 1fr; }
    .cra-title { font-size:1.1rem; }
    .tbl-wrap { overflow-x:auto; display:none !important; }
    .cra-cards { display:block; }
    .mo { max-width:100%; border-radius:16px 16px 0 0; max-height:95vh; }
    .mb { padding:0 10px; align-items:flex-end; }
    .mo-ft { justify-content:stretch; }
    .mo-ft .btn { flex:1; justify-content:center; }
  }
`;

export default function CoordinatorRequestsAdmin() {
  const [requests,    setRequests]    = useState([]);
  const [counts,      setCounts]      = useState({ pending:0 });
  const [allCounts,   setAllCounts]   = useState({ pending:0, approved:0, rejected:0 });
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('all');
  const [actionModal, setActionModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [note,        setNote]        = useState('');
  const [saving,      setSaving]      = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [r, c, all] = await Promise.all([
        api.get(`/admin/coordinator-requests${filter !== 'all' ? `?status=${filter}` : ''}`),
        api.get('/admin/coordinator-requests/counts'),
        api.get('/admin/coordinator-requests'),
      ]);
      setRequests(r.data || []);
      setCounts(c.data || { pending:0 });
      const allData = all.data || [];
      setAllCounts({ pending: allData.filter(r => r.status === 'pending').length, approved: allData.filter(r => r.status === 'approved').length, rejected: allData.filter(r => r.status === 'rejected').length });
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const openAction = (request, action) => { setActionModal({ request, action }); setNote(''); };

  const submitAction = async () => {
    try {
      setSaving(true);
      await api.put(`/admin/coordinator-requests/${actionModal.request._id}`, { action: actionModal.action, reviewNote: note });
      toast.success(actionModal.action === 'approve' ? '✅ Approved and executed' : '❌ Rejected');
      setActionModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const pending = counts.pending || 0;

  return (
    <div className="cra"><style>{css}</style>
      <div className="cra-hdr">
        <div>
          <div className="cra-title">
            Coordinator Requests
            {pending > 0 && <span style={{marginLeft:10,background:'#ef4444',color:'white',borderRadius:99,padding:'2px 10px',fontSize:'.72rem',fontWeight:700,verticalAlign:'middle'}}>{pending} pending</span>}
          </div>
          <div className="cra-sub">Review and approve or reject coordinator requests</div>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="sc-label">Pending</div><div className="sc-value" style={{color:'#f59e0b'}}>{allCounts.pending}</div></div>
        <div className="stat-card"><div className="sc-label">Approved</div><div className="sc-value" style={{color:'#10c98e'}}>{allCounts.approved}</div></div>
        <div className="stat-card"><div className="sc-label">Rejected</div><div className="sc-value" style={{color:'#ef4444'}}>{allCounts.rejected}</div></div>
      </div>

      <div className="tabs">
        {[
          { key:'all',      label:'All' },
          { key:'approved', label:'Approved' },
          { key:'pending',  label:`Pending${pending>0?` (${pending})`:''}` },
          { key:'rejected', label:'Rejected' },
        ].map(t => (
          <button key={t.key} className={`tab${filter===t.key?' on':''}`} onClick={() => setFilter(t.key)}>{t.label}</button>
        ))}
      </div>

      <div className="panel">
        {loading ? <div className="loading"><div className="spin"/>Loading…</div>
        : !requests.length ? <div className="empty">{filter==='pending'?'✅ No pending requests':'No requests found.'}</div>
        : <div className="tbl-wrap" style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead>
                <tr><th>Type</th><th>Coordinator</th><th>Summary</th><th>Status</th><th>Review Note</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {requests.map(req => (
                  <tr key={req._id}>
                    <td style={{fontWeight:600,whiteSpace:'nowrap'}}>{TYPE_LABELS[req.type] || req.type}</td>
                    <td style={{fontSize:'.78rem'}}>{req.coordinator?.name || '—'}</td>
                    <td style={{fontSize:'.76rem',color:'#6b7194',maxWidth:180}}>
                      <span style={{display:'block',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:180}}>
                        {payloadSummary(req.type, req.payload)}
                      </span>
                    </td>
                    <td>
                      <span className={`pill pill-${req.status}`}>
                        {req.status==='pending'  && <ClockIcon />}
                        {req.status==='approved' && <CheckCircleIcon />}
                        {req.status==='rejected' && <XCircleIcon />}
                        {req.status}
                      </span>
                    </td>
                    <td style={{fontSize:'.74rem',color:'#9094a8',maxWidth:160}}>{req.reviewNote || '—'}</td>
                    <td><span className="mono">{fmtDate(req.createdAt)}</span></td>
                    <td>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDetailModal(req)}><EyeIcon /></button>
                        {req.status === 'pending' && <>
                          <button className="btn btn-green btn-sm" onClick={() => openAction(req,'approve')}><CheckCircleIcon />Approve</button>
                          <button className="btn btn-red   btn-sm" onClick={() => openAction(req,'reject')}><XCircleIcon />Reject</button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
        {!loading && !!requests.length && (
          <div className="cra-cards">
            {requests.map(req => (
              <div key={req._id} className="cra-card">
                <div className="cra-card-top">
                  <div>
                    <div className="cra-card-type">{TYPE_LABELS[req.type] || req.type}</div>
                    <div className="cra-card-by">{req.coordinator?.name || '—'} · {fmtDate(req.createdAt)}</div>
                  </div>
                  <span className={`pill pill-${req.status}`}>
                    {req.status==='pending'  && <ClockIcon />}
                    {req.status==='approved' && <CheckCircleIcon />}
                    {req.status==='rejected' && <XCircleIcon />}
                    {req.status}
                  </span>
                </div>
                <div className="cra-card-row"><span className="cra-card-label">Summary</span><span className="cra-card-summary">{payloadSummary(req.type, req.payload)}</span></div>
                {req.reviewNote && <div className="cra-card-row"><span className="cra-card-label">Review Note</span><span className="cra-card-summary">{req.reviewNote}</span></div>}
                <div className="cra-card-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => setDetailModal(req)}><EyeIcon />View</button>
                  {req.status === 'pending' && <>
                    <button className="btn btn-green btn-sm" onClick={() => openAction(req,'approve')}><CheckCircleIcon />Approve</button>
                    <button className="btn btn-red   btn-sm" onClick={() => openAction(req,'reject')}><XCircleIcon />Reject</button>
                  </>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal && createPortal(
        <div className="mb" onClick={() => setDetailModal(null)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">{TYPE_LABELS[detailModal.type] || detailModal.type}</div>
              <button className="mo-x" onClick={() => setDetailModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div style={{fontSize:'.76rem',color:'#9094a8',marginBottom:12}}>
                By <strong style={{color:'#0f1123'}}>{detailModal.coordinator?.name}</strong> · {fmtDate(detailModal.createdAt)}
                <span className={`pill pill-${detailModal.status}`} style={{marginLeft:8}}>
                  {detailModal.status==='pending'  && <ClockIcon />}
                  {detailModal.status==='approved' && <CheckCircleIcon />}
                  {detailModal.status==='rejected' && <XCircleIcon />}
                  {detailModal.status}
                </span>
              </div>
              <PayloadDetail type={detailModal.type} payload={detailModal.payload} />
              {detailModal.reviewNote && <div style={{fontSize:'.76rem',color:'#6b7194',fontStyle:'italic',background:'#f8f9fc',borderRadius:9,padding:'8px 12px'}}>Admin note: {detailModal.reviewNote}</div>}
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setDetailModal(null)}>Close</button>
              {detailModal.status === 'pending' && <>
                <button className="btn btn-green" onClick={() => { setDetailModal(null); openAction(detailModal,'approve'); }}><CheckCircleIcon />Approve</button>
                <button className="btn btn-red"   onClick={() => { setDetailModal(null); openAction(detailModal,'reject');  }}><XCircleIcon />Reject</button>
              </>}
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Action Modal */}
      {actionModal && createPortal(
        <div className="mb" onClick={() => setActionModal(null)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">{actionModal.action === 'approve' ? '✅ Approve Request' : '❌ Reject Request'}</div>
              <button className="mo-x" onClick={() => setActionModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div className={`notice-box ${actionModal.action==='approve'?'notice-approve':'notice-reject'}`}>
                {actionModal.action === 'approve' ? '✅ Approving will immediately execute this action.' : '❌ The coordinator will see this as rejected.'}
              </div>
              <div style={{background:'#f8f9fc',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:'.8rem'}}>
                <strong>{TYPE_LABELS[actionModal.request.type]}</strong> by {actionModal.request.coordinator?.name}
                <div style={{color:'#6b7194',marginTop:4,fontSize:'.76rem'}}>{payloadSummary(actionModal.request.type, actionModal.request.payload)}</div>
              </div>
              <div className="ff">
                <label className="fl">{actionModal.action==='reject' ? 'Rejection Reason *' : 'Note (optional)'}</label>
                <textarea className="ft" value={note} onChange={e => setNote(e.target.value)} placeholder={actionModal.action==='approve' ? 'e.g. Approved and created.' : 'e.g. Duplicate entry, please verify.'} />
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setActionModal(null)}>Cancel</button>
              {actionModal.action === 'approve'
                ? <button className="btn btn-green" onClick={submitAction} disabled={saving}><CheckCircleIcon />{saving?'Approving…':'Approve'}</button>
                : <button className="btn btn-red"   onClick={submitAction} disabled={saving || !note.trim()}><XCircleIcon />{saving?'Rejecting…':'Reject'}</button>}
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}